import { appStorage } from '../lib/safeStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, ArrowRight, Sparkles, LayoutDashboard, Columns3, Users, Building2 } from 'lucide-react';
import { isCompanyUser } from '../lib/roleRoutes';

/**
 * OnboardingTour — a single, unified 5-step "welcome" walkthrough.
 *
 * One system for everyone now, so one tour. It teaches the core loop (lead →
 * booked event → staffed) rather than naming every sidebar section.
 *
 * Style: centered premium modal (NOT DOM-spotlight) so it can't break when the
 * nav changes. Each step offers a "Take me there" shortcut.
 *
 * Persistence (owner's call): auto-shows ONCE for operator (company) users
 * only, keyed on `staffed_tour_seen` (honors legacy `staffed_tour_completed`).
 * After finishing or dismissing it never auto-shows again, but is always
 * re-launchable via window CustomEvent 'staffed:start-tour' (Help Center button).
 */

const SEEN_KEY = 'staffed_tour_seen';
const LEGACY_DONE_KEY = 'staffed_tour_completed';
const START_EVENT = 'staffed:start-tour';

const STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    title: 'Welcome to STAFFED',
    content:
      "Your command center for winning events and running the team behind them. Everything lives in the sidebar on your left — here's the 60-second tour. You can end it anytime.",
  },
  {
    id: 'command-deck',
    icon: LayoutDashboard,
    title: 'Your Command Deck',
    content:
      'Your home base. Every morning it shows what needs you — upcoming events, staffing gaps, and quick actions, all in one glance.',
    route: '/control-center',
  },
  {
    id: 'lead-to-event',
    icon: Columns3,
    title: 'From Lead to Booked Event',
    content:
      'Leads come in through your pipeline — QR codes, your booking form, live leads — and each one runs the full client journey: proposal, contract, payment, and planning, right up to a booked event with its own workspace.',
    route: '/events/pipeline',
  },
  {
    id: 'staff',
    icon: Users,
    title: 'Staff Every Event',
    content:
      "Set your team's roles once, then staff any event in a tap. Track availability, Heat Points, and payouts as the work happens.",
    route: '/staff',
  },
  {
    id: 'finish',
    icon: Building2,
    title: "Make It Yours",
    content:
      "Add your branding and payment settings anytime under Company in the sidebar. You're all set — create your first event to dive in. Replay this tour whenever from the Help Center.",
    route: '/events',
    finishCtaLabel: 'Go to Events',
  },
];

const OnboardingTour = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const openTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  // Auto-show ONCE (operator users only); always listen for on-demand relaunch.
  useEffect(() => {
    const onStart = () => openTour();
    window.addEventListener(START_EVENT, onStart);

    let isCompany = false;
    try { isCompany = isCompanyUser(JSON.parse(appStorage.getItem('user') || '{}')); } catch (e) { /* skip */ }
    const alreadySeen = appStorage.getItem(SEEN_KEY) || appStorage.getItem(LEGACY_DONE_KEY);

    let timer;
    if (isCompany && !alreadySeen) {
      timer = setTimeout(openTour, 900);
    }
    return () => {
      window.removeEventListener(START_EVENT, onStart);
      if (timer) clearTimeout(timer);
    };
  }, [openTour]);

  const markDoneAndClose = useCallback(() => {
    try {
      appStorage.setItem(SEEN_KEY, 'true');
      appStorage.setItem(LEGACY_DONE_KEY, 'true'); // back-compat
    } catch (e) { /* ignore */ }
    setIsActive(false);
  }, []);

  if (!isActive) return null;

  const step = STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;
  const Icon = step.icon || Sparkles;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep((s) => s + 1);
    } else {
      if (step.route) navigate(step.route);
      markDoneAndClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleTakeMeThere = () => {
    if (step.route) navigate(step.route);
    markDoneAndClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="onboarding-tour">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-300"
        onClick={markDoneAndClose}
        data-testid="onboarding-tour-backdrop"
      />

      <div
        className="relative w-full max-w-[420px] rounded-2xl overflow-hidden animate-fade-in-up backdrop-blur-xl"
        role="dialog"
        aria-modal="true"
        style={{
          background: 'linear-gradient(145deg, rgba(18,22,42,0.92), rgba(10,13,28,0.96))',
          border: '1px solid rgba(212,175,55,0.22)',
          boxShadow:
            '0 24px 60px -12px rgba(0,0,0,0.65), 0 0 28px rgba(212,175,55,0.10), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Soft gold glow accent for depth (glassy, not flat) */}
        <div
          className="pointer-events-none absolute inset-x-0 -top-10 h-32"
          style={{ background: 'radial-gradient(60% 80% at 50% 0%, rgba(212,175,55,0.18), rgba(212,175,55,0))' }}
        />

        <div
          className="relative p-5 border-b border-[#D4AF37]/25"
          style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.20), rgba(229,199,107,0.06) 60%, rgba(255,255,255,0))',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
        >
          <button
            onClick={markDoneAndClose}
            className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="onboarding-tour-close"
            aria-label="End tour"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 shadow-lg shadow-[#D4AF37]/20">
              <Icon className="w-5 h-5 text-[#E5C76B]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white leading-tight" data-testid="onboarding-tour-title">{step.title}</h3>
              <p className="text-xs text-gray-400 mt-0.5">Step {currentStep + 1} of {STEPS.length}</p>
            </div>
          </div>
        </div>

        <div className="p-5">
          <p className="text-gray-300 text-sm leading-relaxed">{step.content}</p>

          {step.route && !isLastStep && (
            <button
              onClick={handleTakeMeThere}
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#E5C76B] hover:text-[#F0D584] transition-colors"
              data-testid="onboarding-tour-takemethere"
            >
              Take me there <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="px-5 pb-4 flex items-center justify-between">
          <button
            onClick={markDoneAndClose}
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            data-testid="onboarding-tour-end"
          >
            {isLastStep ? 'Close' : 'End tour'}
          </button>

          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors flex items-center gap-1"
                data-testid="onboarding-tour-back"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-5 py-2 bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0B0F1A] text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all flex items-center gap-1"
              data-testid="onboarding-tour-next"
            >
              {isLastStep ? (step.finishCtaLabel || 'Get Started') : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="px-5 pb-4 flex justify-center gap-1.5">
          {STEPS.map((_, idx) => (
            <div
              key={idx}
              className={`h-2 rounded-full transition-all ${
                idx === currentStep
                  ? 'bg-[#D4AF37] w-6'
                  : idx < currentStep
                  ? 'bg-[#D4AF37]/50 w-2'
                  : 'bg-white/20 w-2'
              }`}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingTour;
